import type { CardsPriorityOptions, CardsStatusOptions } from "@/lib/pb-types";

/**
 * The seven lanes, in board order, and how the board is allowed to use colour.
 *
 * These are the only values `cards.status` accepts (the schema locks the select
 * to exactly this list), so the array is both the render order and the
 * validation set. Anything that needs "is this a real lane?" asks `isLaneStatus`
 * rather than re-listing the strings.
 *
 * ## Colour lives on a different axis than it used to (UI-REVIEW P8)
 *
 * The lanes used to borrow the app's semantic accents: To Do and In Review were
 * both `plan` blue, Backlog and Archived were both grey, In Progress was the
 * same amber as an overdue task, Done the same sage as the primary button, and
 * Blocking the same red as delete. Four of seven lanes were indistinguishable
 * and none of the colours meant one thing.
 *
 * They now use `--lane-*`, a dedicated set of muted, surface-aware Material
 * hues. The colours remain distinguishable without borrowing the saturated
 * semantic accents used for actions, warnings and streaks.
 *
 * The **issue key is no longer tinted by lane**. It is an identifier, not a
 * status, and the column it sits in already says which lane it is in; that chip
 * carried exactly zero bits. The colour it was spending moved onto priority,
 * which is what you actually scan a board for.
 *
 * ## Deliberately no runtime imports
 *
 * `board.ts` imports this file and `board.test.ts` imports `board.ts`, so
 * anything pulled in here is loaded by the test suite. Class names are written
 * out in full instead — which Tailwind requires anyway, since it reads source
 * text and `bg-lane-${status}` produces nothing.
 */

export type LaneStatus = CardsStatusOptions;
export type CardPriority = CardsPriorityOptions;

export type Lane = {
  status: LaneStatus;
  title: string;
  /** One line of intent, shown when the lane is empty. */
  blurb: string;
  /**
   * The lane's own ordinal colour as a fill: the drop line, the collapsed rail's
   * mark.
   *
   * Fill only, never text. The ramp is tuned so each step clears 3:1 against
   * the surface — enough for a marker, and deliberately not enough to be typed
   * on. Lane names are `text-ink` and `text-muted` like every other name.
   */
  marker: string;
  /** Tint plus a 3:1 edge, drawn while a card is hovering over the lane. */
  active: string;
  /**
   * History rather than work. These two collapse to a 48px rail by default:
   * seven full-width lanes never fit a real monitor, and on a board with 67
   * cards in Backlog, Done and Archived are the two you look at least.
   */
  archiveLike: boolean;
};

export const LANES: readonly Lane[] = [
  {
    status: "backlog",
    title: "Backlog",
    blurb: "Not started, not scheduled.",
    marker: "bg-lane-backlog",
    active: "border-lane-backlog bg-lane-backlog-soft",
    archiveLike: false,
  },
  {
    status: "to-do",
    title: "To Do",
    blurb: "Queued up and ready.",
    marker: "bg-lane-to-do",
    active: "border-lane-to-do bg-lane-to-do-soft",
    archiveLike: false,
  },
  {
    status: "blocking",
    title: "Blocking",
    blurb: "Waiting on something else.",
    marker: "bg-lane-blocking",
    active: "border-lane-blocking bg-lane-blocking-soft",
    archiveLike: false,
  },
  {
    status: "in-progress",
    title: "In Progress",
    blurb: "Actively being worked on.",
    marker: "bg-lane-in-progress",
    active: "border-lane-in-progress bg-lane-in-progress-soft",
    archiveLike: false,
  },
  {
    status: "in-review",
    title: "In Review",
    blurb: "Done, pending a look.",
    marker: "bg-lane-in-review",
    active: "border-lane-in-review bg-lane-in-review-soft",
    archiveLike: false,
  },
  {
    status: "done",
    title: "Done",
    blurb: "Finished.",
    marker: "bg-lane-done",
    active: "border-lane-done bg-lane-done-soft",
    archiveLike: true,
  },
  {
    status: "archived",
    title: "Archived",
    blurb: "Kept for history.",
    marker: "bg-lane-archived",
    active: "border-lane-archived bg-lane-archived-soft",
    archiveLike: true,
  },
] as const;

export const LANE_STATUSES: readonly LaneStatus[] = LANES.map((lane) => lane.status);

const LANE_BY_STATUS = new Map<string, Lane>(LANES.map((lane) => [lane.status, lane]));

export function isLaneStatus(value: unknown): value is LaneStatus {
  return typeof value === "string" && LANE_BY_STATUS.has(value);
}

/**
 * The lane a status belongs to. Falls back to Backlog rather than throwing: a
 * row that somehow carries an unknown status must still be reachable on screen,
 * because a card you cannot see is a card you cannot fix.
 */
export function laneFor(status: string): Lane {
  return LANE_BY_STATUS.get(status) ?? LANES[0]!;
}

/** Moving a card here is the move worth offering an undo for (UI-REVIEW P10). */
export function isTerminalStatus(status: LaneStatus): boolean {
  return status === "done" || status === "archived";
}

/* -------------------------------------------------------------------------- */
/* Priority                                                                    */

export const PRIORITIES: readonly CardPriority[] = ["critical", "high", "medium", "low"];

export function isPriority(value: unknown): value is CardPriority {
  return typeof value === "string" && (PRIORITIES as readonly string[]).includes(value);
}

/**
 * How a priority is allowed to be drawn — and, for two of the four, that it is
 * not drawn at all.
 *
 * UI-REVIEW P8: "`CRITICAL` a solid filled pill, `HIGH` a tinted pill,
 * `MEDIUM` and `LOW` nothing at all. Absence is the strongest signal available
 * and it is currently unused." Priority used to be bare uppercase micro text in
 * a fourth colour scale, on every single card, which is the same as saying
 * nothing on any of them.
 *
 * What ships is the **6px dot** rather than a pill, which is P9's wording for
 * the same decision ("priority as a 6px dot on the left") and is the better
 * encoding at this density: a fixed-position dot repeating down a lane of 67
 * cards is scannable in one eye movement, where a pill of varying width on a
 * varying second line is not, and a 215px lane cannot spend 70px of it on the
 * word "CRITICAL" without pushing the owner and the due date off the card.
 * Critical and high differ by hue — red and amber, neither of which the lane
 * ramp uses — and medium and low are drawn as nothing at all.
 *
 * Colour is never the only channel: every tile's accessible name states the
 * priority in words, and the peek states it as a selected segment.
 */
export type PriorityMark = {
  /** Sentence case, for a label a person reads. */
  label: string;
  /** The 6px dot on the tile. */
  dot: string;
};

export const PRIORITY_MARK: Record<CardPriority, PriorityMark | null> = {
  critical: { label: "Critical", dot: "bg-danger" },
  high: { label: "High", dot: "bg-streak" },
  medium: null,
  low: null,
};

/** The label for any priority, including the two that are drawn as nothing. */
export function priorityLabel(priority: string): string {
  if (!isPriority(priority)) {
    return "No priority";
  }
  return PRIORITY_MARK[priority]?.label ?? priority[0]!.toUpperCase() + priority.slice(1);
}

/** Card types seen in the source vault, offered as suggestions rather than a select. */
export const TYPE_SUGGESTIONS: readonly string[] = [
  "task",
  "bug",
  "feature",
  "chore",
  "decision",
  "idea",
  "spike",
  "docs",
];
